import React, { Component } from 'react';
import isFunction from 'lodash/isFunction';
import Carrier from '../../carrier';
import { AccountBalanceOptions, PostDateOptions } from '../../data/settings';
import Payment from '../../payment';
import AccountVerification from '../../accountVerification';
import UI from '../../ui';
import Utils from '../../utils';
import CarrierStatementsModal from './CarrierStatementsModal.component';
import MeterContactInfoModal from './MeterContactInfoModal.component';
import ShipNotificationModal from './ShipNotificationModal.component';
import Util from '../Util';
import { Capability, InternalCapability } from '../../data/account';

const { Util: CarrierUtil } = Carrier;
const { AutoFundAccountModal } = Payment;
const { AccountVerificationModal } = AccountVerification;
const { WrappedButton, WrappedDropdownWithLabel, WrappedCheckbox, WrappedRadioButton } = UI;
const { Communication, Logger, Session, withLayoutAndModalContexts } = Utils;

class GeneralView extends Component {
	constructor(props) {
		super(props);

		const {
			Postdate = '17',
			PostageBalance = '0',
			AutoUpdateIntlAddress = '0',
			EmailTo = '0',
			RateAdvisorTriggerDisplay = '2',
		} = Session.getPreferences();

		this.state = {
			postdate: Postdate,
			postageBalance: PostageBalance,
			carrierCustomsAgreements: CarrierUtil.hasUserAcceptedAllCarrierAgreements(),
			autoUpdateIntlAddress: AutoUpdateIntlAddress,
			emailTo: EmailTo,
			customerEmail: '',
			rateAdvisorTriggerDisplay: RateAdvisorTriggerDisplay,
		};
	}

	async componentDidMount() {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await Communication.getCustomerContactInfo();

		if (Communication.isSuccessful(response)) {
			const { Email = '' } = response.data.AccountContactInfo || {};

			this.setState({ customerEmail: Email });
		} else {
			showError({ response });
		}
	}

	changeEmailCallback = newEmail => {
		this.setState({
			customerEmail: newEmail,
		});
	};

	onChangeDropdown = ({ target: { name }, value }) => {
		const {
			modalContext: { showError },
		} = this.props;
		const newPreferences = {
			[name]: value,
		};

		this.setState(newPreferences);

		Util.saveNewPreferences(newPreferences, showError);
	};

	onChangeCheckbox = ({ target: { name } }) => {
		const { [name]: value } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		/**
		 * For backward compatibility, the checkbox is also checked if the value is 'true'.
		 * Moving forward, we should only store '1' or '0' for checked or unchecked.
		 */
		let newValue = value === '0' || value === false ? '1' : '0';
		let newSettingsValue = newValue;

		if (name === 'carrierCustomsAgreements') {
			newValue = !value;
			newSettingsValue = JSON.stringify(CarrierUtil.updatedCarrierAgreements(newValue));
		}

		this.setState({
			[name]: newValue,
		});

		Util.saveNewPreferences({ [name]: newSettingsValue }, showError);
	};

	onClickShipNotification = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: ShipNotificationModal,
		});
	};

	onChangeRadioButtons = ({ value }) => {
		const {
			modalContext: { showError },
		} = this.props;

		const newPreferences = {
			rateAdvisorTriggerDisplay: value,
		};

		this.setState(newPreferences);

		Util.saveNewPreferences(newPreferences, showError);
	};

	onClickDone = () => {
		const { rateAdvisorTriggerDisplay } = this.state;
		const { completionCallback } = this.props;
		const canUseRateAdvisor = Session.hasCapability(InternalCapability.RateAdvisor);

		const data = canUseRateAdvisor ? { rateAdvisorTriggerDisplay } : {};

		if (isFunction(completionCallback)) {
			completionCallback({ close: true, ...data });
		}
	};

	/**
	 * Triggers account verification if user has not been verified yet.
	 * Launches autoFundModal if/once verified.
	 */
	autoBuyPostage = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		if (Session.isAccountVerificationRequired()) {
			Logger.logToServer({
				category: 'accountVerification',
				verificationStep: 'prompt',
				prompt: 'autoFund',
			});

			return showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.launchAutoFundAccountModal,
				},
			});
		}

		this.launchAutoFundAccountModal();
	};

	launchAutoFundAccountModal = () => {
		const {
			modalContext: { showModal },
			completionCallback,
		} = this.props;

		showModal({
			component: AutoFundAccountModal,
			data: {
				completionCallback,
			},
		});
	};

	launchCarrierStatementsModal = e => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: CarrierStatementsModal,
		});

		e.stopPropagation();
		e.preventDefault();
	};

	launchMeterContactInfoModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: MeterContactInfoModal,
			data: {
				changeEmailCallback: this.changeEmailCallback,
			},
		});
	};

	renderEmailField = () => {
		const { customerEmail } = this.state;

		return (
			<>
				<div
					className="settings-inline"
					data-qa="settings-deliveriesemail"
					{...{ dataqa: 'settings-deliveriesemail' }}
				>
					{customerEmail}
				</div>
				<a
					className="settings-inline"
					onClick={this.launchMeterContactInfoModal}
					role="button"
					tabIndex={0}
					data-qa="settings-deliveries-edit"
					{...{ dataqa: 'settings-deliveries-edit' }}
				>
					Edit
				</a>
			</>
		);
	};

	render() {
		const {
			postdate,
			postageBalance,
			carrierCustomsAgreements,
			autoUpdateIntlAddress,
			emailTo,
			rateAdvisorTriggerDisplay,
		} = this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;

		const canPurchasePostage = Session.hasCapability(Capability.AddFunds);
		const canUseBrandedEmails = Session.hasCapability(Capability.BrandedEmails);
		const canUseRateAdvisor = Session.hasCapability(InternalCapability.RateAdvisor);

		const isIntlVerificationChecked =
			autoUpdateIntlAddress === '1' || autoUpdateIntlAddress === true;
		const isDeliveriesChecked = emailTo === '1' || emailTo === true;
		const mobileCls = isMobile ? 'mobile-view' : '';

		return (
			<div className="settings-common-general-view">
				<div className="settings-body">
					<div
						className="settings-content-header"
						data-qa="settings-generalsettings-header"
						{...{ dataqa: 'settings-generalsettings-header' }}
					>
						<p>General Settings</p>
						<hr />
					</div>
					<div className="p-grid p-dir-col">
						<div
							className={`p-col-12 ${mobileCls}`}
							data-qa="settings-postdate-wrapper"
							{...{ dataqa: 'settings-postdate-wrapper' }}
						>
							<div
								className={`settings-label ${mobileCls}`}
								data-qa="settings-postdate-label"
								{...{ dataqa: 'settings-postdate-label' }}
							>
								Postdate:
							</div>
							<WrappedDropdownWithLabel
								className={mobileCls}
								label="Postdate mail to next day after"
								uiElementConfig={{
									className: 'postdate-dropdown',
									name: 'postdate',
									value: postdate,
									options: PostDateOptions,
									optionLabel: 'label',
									optionValue: 'value',
									onChange: this.onChangeDropdown,
									dataqa: 'settings-postdate',
								}}
							/>
						</div>
						<div
							className={`p-col-12 ${mobileCls}`}
							data-qa="settings-postagebalance-wrapper"
							{...{ dataqa: 'settings-postagebalance-wrapper' }}
						>
							<div
								className={`settings-label ${mobileCls}`}
								data-qa="settings-accountbalance-label"
								{...{ dataqa: 'settings-accountbalance-label' }}
							>
								Account Balance:
							</div>
							<WrappedDropdownWithLabel
								className={mobileCls}
								label="Notify me when account balance drops below"
								uiElementConfig={{
									name: 'postageBalance',
									value: postageBalance,
									options: AccountBalanceOptions,
									optionLabel: 'label',
									optionValue: 'value',
									onChange: this.onChangeDropdown,
									dataqa: 'settings-postagebalance',
								}}
							/>
							{canPurchasePostage && (
								<a
									className="settings-inline"
									onClick={this.autoBuyPostage}
									role="link"
									tabIndex={0}
									data-qa="settings-autofundlink"
									{...{ dataqa: 'settings-autofundlink' }}
								>
									Auto-fund account
								</a>
							)}
						</div>
						<div className={`p-col-12 ${mobileCls}`}>
							<div
								className={`settings-label ${mobileCls}`}
								data-qa="settings-carrierterms-label"
								{...{ dataqa: 'settings-carrierterms-label' }}
							>
								Carrier Terms:
							</div>
							<WrappedCheckbox
								inputId="settings-general-carrier-terms"
								name="carrierCustomsAgreements"
								value={carrierCustomsAgreements}
								onChange={this.onChangeCheckbox}
								checked={carrierCustomsAgreements}
								dataqa="settings-carriercustomsagreements"
							>
								<p
									data-qa="settings-carriertermslinktext"
									{...{ dataqa: 'settings-carriertermslinktext' }}
								>
									I acknowledge and agree to the{' '}
									<a
										onClick={this.launchCarrierStatementsModal}
										role="link"
										tabIndex={0}
										data-qa="settings-carrierstatementslink"
										{...{ dataqa: 'settings-carrierstatementslink' }}
									>
										Statements
									</a>{' '}
									of my added carriers
								</p>
							</WrappedCheckbox>
						</div>
						<div className={`p-col-12 ${mobileCls}`}>
							<div
								className={`settings-label ${mobileCls}`}
								data-qa="settings-intlverification-label"
								{...{ dataqa: 'settings-intlverification-label' }}
							>
								Int'l Verification:
							</div>
							<WrappedCheckbox
								inputId="settings-general-intl-verification"
								name="autoUpdateIntlAddress"
								value={autoUpdateIntlAddress}
								onChange={this.onChangeCheckbox}
								checked={isIntlVerificationChecked}
								dataqa="settings-intlverification"
							>
								Automatically update international addresses during address
								verification
							</WrappedCheckbox>
						</div>
					</div>
					<div
						className="settings-content-header"
						data-qa="settings-emailnotifications-label"
						{...{ dataqa: 'settings-emailnotifications-label' }}
					>
						<p>Email Notifications</p>
						<hr />
					</div>
					<div className="p-grid p-dir-col">
						<div
							className={`p-col-12 ${mobileCls}`}
							data-qa="settings-emailnotificationsheadertext"
							{...{ dataqa: 'settings-emailnotificationsheadertext' }}
						>
							<p>
								To personalize your shipment notification emails go to the{' '}
								<a
									onClick={this.onClickShipNotification}
									role="link"
									tabIndex={0}
									data-qa="settings-shipnotification-link"
									{...{ dataqa: 'settings-shipnotification-link' }}
								>
									Ship Notification Email
								</a>{' '}
								page.
							</p>
						</div>
						{!canUseBrandedEmails && (
							<div className={`p-col-12 ${mobileCls}`}>
								<div
									className={`settings-label ${mobileCls}`}
									data-qa="settings-deliveries-label"
									{...{ dataqa: 'settings-deliveries-label' }}
								>
									Deliveries:
								</div>
								<WrappedCheckbox
									inputId="settings-general-deliveries"
									name="emailTo"
									value={emailTo}
									onChange={this.onChangeCheckbox}
									checked={isDeliveriesChecked}
									dataqa="settings-deliveries"
								>
									Send an email when a shipment has been delivered to:
								</WrappedCheckbox>
								{this.renderEmailField()}
							</div>
						)}
					</div>
					{canUseRateAdvisor && (
						<>
							<div
								className="settings-content-header"
								data-qa="settings-rateadvisor-label"
								{...{ dataqa: 'settings-rateadvisor-label' }}
							>
								<p>Rate Advisor</p>
								<hr />
							</div>
							<div
								className="p-grid p-dir-col"
								data-qa="settings-rateadvisor-checkboxgroup"
								{...{ dataqa: 'settings-rateadvisor-checkboxgroup' }}
							>
								<div className={`p-col-12 ${mobileCls}`}>
									<WrappedRadioButton
										inputId="rateadvisor-displaycomparison"
										name="displayComparison"
										value="2"
										onChange={this.onChangeRadioButtons}
										dataqa="rateadvisor-displaycomparison"
										checked={rateAdvisorTriggerDisplay === '2'}
									>
										Display "Browse More Rates..." button in Carrier and Service
										dropdowns
									</WrappedRadioButton>
								</div>
								<div className={`p-col-12 ${mobileCls}`}>
									<WrappedRadioButton
										inputId="rateadvisor-displayshortcut"
										name="displayShortcut"
										value="1"
										onChange={this.onChangeRadioButtons}
										dataqa="rateadvisor-displayshortcut"
										checked={rateAdvisorTriggerDisplay === '1'}
									>
										Display Rate Advisor shortcut button
									</WrappedRadioButton>
								</div>
								<div className={`p-col-12 ${mobileCls}`}>
									<WrappedRadioButton
										inputId="rateadvisor-displaynone"
										name="displayNone"
										value="0"
										onChange={this.onChangeRadioButtons}
										dataqa="rateadvisor-displaynone"
										checked={rateAdvisorTriggerDisplay === '0'}
									>
										Don't display any Rate Advisor buttons
									</WrappedRadioButton>
								</div>
							</div>
						</>
					)}
				</div>
				<div className="settings-content-footer">
					<WrappedButton
						className="settings-button"
						label="Done"
						type="button"
						onClick={this.onClickDone}
						dataqa="savesettings"
					/>
				</div>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(GeneralView);
